/**
 * KAN-858: прокрутка ленты переписки в приложении менеджера:
 *   - новое последнее сообщение (или первая загрузка) -> лента внизу;
 *   - ранние сообщения добавились сверху -> позиция сохраняется (не прыгает);
 *   - прокрутка к верху (ближе SCROLL_TOP_PX) -> onReachTop (загрузить ранние).
 */
import { useCallback, useLayoutEffect, useRef, type UIEvent } from 'react';

const SCROLL_TOP_PX = 48;

interface Options {
  /** id последнего сообщения: сменился -> вниз. */
  lastId: string | undefined;
  /** id первого сообщения: сменился при том же последнем -> добавились ранние. */
  firstId: string | undefined;
  onReachTop: () => void;
}

export function useChatFeedScroll({ lastId, firstId, onReachTop }: Options) {
  const ref = useRef<HTMLDivElement>(null);
  const prev = useRef<{ lastId?: string; firstId?: string; height: number; top: number }>({ height: 0, top: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const before = prev.current;
    if (lastId !== before.lastId) {
      el.scrollTop = el.scrollHeight;
    } else if (firstId !== before.firstId) {
      el.scrollTop = el.scrollHeight - before.height + before.top;
    }
    prev.current = { lastId, firstId, height: el.scrollHeight, top: el.scrollTop };
  }, [lastId, firstId]);

  const onScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      prev.current = { ...prev.current, height: el.scrollHeight, top: el.scrollTop };
      if (el.scrollTop < SCROLL_TOP_PX) onReachTop();
    },
    [onReachTop]
  );

  return { ref, onScroll };
}
